/**
 * JWT Token Utilities
 * 
 * Helper functions to decode and check JWT token expiration
 */

interface JWTPayload {
  sub: number;
  email: string;
  iat?: number;
  exp?: number;
}

/**
 * Decode a JWT token without verification
 * Note: This only decodes the payload, it does NOT verify the signature
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * Returns true if token is expired or invalid, false if still valid
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    // If we can't decode or there's no expiration, consider it invalid
    return true;
  }
  
  // exp is in seconds, Date.now() is in milliseconds
  const expirationTime = payload.exp * 1000;
  const now = Date.now();
  
  return now >= expirationTime;
}

/**
 * Get the expiration time of a JWT token in milliseconds
 * Returns null if token is invalid or has no expiration
 */
export function getTokenExpiration(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }
  
  return payload.exp * 1000;
}

/**
 * Get time until token expiration in milliseconds
 * Returns negative number if expired, positive if still valid
 */
export function getTimeUntilExpiration(token: string): number | null {
  const expiration = getTokenExpiration(token);
  if (expiration === null) {
    return null;
  }
  
  return expiration - Date.now();
}

/**
 * Format time until expiration as human-readable string
 */
export function formatTimeUntilExpiration(token: string): string {
  const timeUntil = getTimeUntilExpiration(token);
  if (timeUntil === null) {
    return 'Unknown';
  }
  
  if (timeUntil < 0) {
    const daysExpired = Math.abs(Math.floor(timeUntil / (1000 * 60 * 60 * 24)));
    return `Expired ${daysExpired} day${daysExpired !== 1 ? 's' : ''} ago`;
  }
  
  const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else {
    const minutes = Math.floor(timeUntil / (1000 * 60));
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
}

